import Component from '@ember/component';
import { computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { prependEventstreamCatalogToKeys } from '@square/ember-plaid-link/utils/prepend-to-keys';
import layout from '../templates/components/plaid-link';

/**
 * Ember Plaid Link
 * An Ember wrapper around the Plaid Link V2 module: https://plaid.com/docs/#integrating-with-link
 *
 * Example usage:
 * hbs```
    {{plaid-link
      onSuccess=(action "onSuccess")
      onExit=(action "onExit")
      onEvent=(action "onEvent")
      onLoad=(action "onLoad")
      onOpen=(action "onOpen")
      fetchToken=(action "fetchToken")
      onInjectError=(action "onInjectError")
      onClickError=(action "onClickError")
      customerId=customer.id}}
 * ```
 *
 * Parameters for the component:
 * @param customerId
 * @param onSuccess
 * @param onExit
 * @param onLoad
 * @param onEvent
 * @param onOpen
 * @param fetchToken
 * @param onInjectError
 * @param onClickError
 *
 * Parameters for the configuration (can be included in a config file or in the component declaration):
 * @param clientName
 * @param product
 * @param key
 * @param env
 * @param webhook
 * @param token
 * @param countryCodes [optional]
 * @param receivedRedirectUri
 */

const OPTIONS = ['clientName', 'product', 'key', 'env', 'onSuccess', 'onExit', 'onEvent', 'onLoad', 'webhook', 'token', 'countryCodes', 'receivedRedirectUri'];
const DEFAULT_LABEL = 'Link Bank Account'; // Displayed on button if no block is passed to component
const CATALOG_NAME = 'ember_plaid_link';
export default Component.extend({
  layout,
  tagName: 'button',
  plaidClient: service(),
  eventstream: service(),
  label: DEFAULT_LABEL,
  // Overridden if component is used in block format

  // Passed in for eventstream
  customerId: null,
  // Custom Link action parameters to pass into component via view
  fetchToken: null,
  // default to null so we can check for its presence
  onOpen() {},
  onInjectError() {},
  onClickError() {},
  // Plaid Link action Parameters to pass into component via view
  // Complete documentation: https://plaid.com/docs/api/#parameter-reference
  onSuccess() {},
  onExit() {},
  onLoad() {},
  onEvent() {},
  // A callback that is fired to capture all non-error events emitted by the
  // plaid modal. It is intended to be used for tracking purposes - ie: by
  // passing a function that sends events to CDP.
  //
  // Includes:
  // - onSuccess - Necessary to capture success events
  // - onLoad - Necessary to capture load events
  // - onEvent - Captures all other events emitted by the modal
  // - onInjectError - Captures error messages
  // - onClickError - Captures error messages
  //
  // Does not include:
  // - onExit - There is an onEvent equivalent with eventName "EXIT"
  // - onOpen - There is an onEvent equivalent with eventName "OPEN"
  onAllEvents() {},
  // Link Parameters to pass into component via view or config file
  // Complete documentation: https://plaid.com/docs/api/#parameter-reference
  clientName: null,
  product: null,
  key: null,
  env: null,
  webhook: null,
  token: null,
  countryCodes: null,
  receivedRedirectUri: null,
  // Private
  options: computed(...OPTIONS, function () {
    return Object.assign(this.getProperties(OPTIONS), {
      // bind `this` context to access eventstream service in the callback
      onEvent: this.onEventTracking.bind(this),
      onSuccess: this.onSuccessTracking.bind(this),
      onLoad: this.onLoadTracking.bind(this)
    });
  }),
  init() {
    this._super(...arguments);
    this.plaidClient.injectScript().catch(error => this.onInjectErrorTracking(error));

    // If a receivedRedirectUri was passed in, this means Plaid has redirected from an OAuth flow,
    // so open Plaid automatically to complete the link instead of forcing a button click.
    if (this.receivedRedirectUri) {
      this.set('receivedRedirectUri', this.receivedRedirectUri);
      this.fetchToken().then(token => {
        this.set('token', token);
        // If we have a token, we don't want to also pass in a key
        this.set('key', null);
        this.plaidClient.open(this.options).then(() => this.onOpen()).catch(error => this.onClickErrorTracking(error));
      });
    }
  },
  click() {
    if (this.fetchToken) {
      this.fetchToken().then(token => {
        this.set('token', token);
        // If we have a token, we don't want to also pass in a key
        this.set('key', null);
        this.plaidClient.open(this.options).then(() => this.onOpen()).catch(error => this.onClickErrorTracking(error));
      });
    } else {
      this.plaidClient.open(this.options).then(() => this.onOpen()).catch(error => this.onClickErrorTracking(error));
    }
  },
  onLoadTracking() {
    this.onAllEvents("LOAD", {});
    this.onLoad();
  },
  onInjectErrorTracking(error) {
    this.onAllEvents("INJECT_ERROR", {
      error_type: error.name,
      error_message: error.message
    });
    this.onInjectError(error);
  },
  onClickErrorTracking(error) {
    this.onAllEvents("CLICK_ERROR", {
      error_type: error.name,
      error_message: error.message
    });
    this.onClickError(error);
  },
  // For tracking all eventstream data. Calls onEvent (passed in by
  // consuming app) so that the app can perform something in addition
  // to the es2 event tracking.
  // https://plaid.com/docs/#onevent-callback
  onEventTracking(eventName, metadata) {
    const prependedPlaidMetadata = prependEventstreamCatalogToKeys(CATALOG_NAME, Object.assign({
      event_name: eventName,
      customer_id: this.customerId
    }, metadata));
    this.trackWithEventstream(prependedPlaidMetadata);
    this.onEvent(eventName, metadata);
    this.onAllEvents(eventName, metadata);
  },
  // For tracking eventstream data on success. Calls onSuccess (passed in by
  // consuming app) so that the app can perform something in addition
  // to the es2 event tracking.
  // https://plaid.com/docs/#onsuccess-callback
  onSuccessTracking(publicToken, metadata) {
    let accountId = null;
    let accountType = null;
    let accountSubtype = null;
    let institutionId = null;
    let institutionName = null;

    // transactions api will return accounts
    if (metadata.accounts && metadata.accounts.length) {
      accountId = metadata.accounts[0].id;
      accountType = metadata.accounts[0].type;
      accountSubtype = metadata.accounts[0].subtype;
    }

    // sba_verification api should return data_source
    if (metadata.data_source) {
      institutionName = metadata.data_source;
    } else if (metadata.institution) {
      institutionName = metadata.institution.name;
      institutionId = metadata.institution.institution_id;
    }
    const flattenedMetadata = {
      event_name: "SUCCESS",
      customer_id: this.customerId,
      link_session_id: metadata.link_session_id || null,
      institution_name: institutionName,
      institution_id: institutionId,
      account_id: accountId,
      account_type: accountType,
      account_subtype: accountSubtype
    };
    const prependedPlaidMetadata = prependEventstreamCatalogToKeys(CATALOG_NAME, flattenedMetadata);
    this.trackWithEventstream(prependedPlaidMetadata);
    this.onAllEvents("SUCCESS", flattenedMetadata);
    this.onSuccess(publicToken, metadata);
  },
  // Call trackV2WithDefaults or track() if implemented to work with dashboard eventstream.
  // Otherwise, do not track
  trackWithEventstream(prependedPlaidMetadata) {
    const eventStream = this.eventstream;
    if (eventStream && typeof eventStream.get('trackWithDefaults') === 'function') {
      eventStream.trackWithDefaults(CATALOG_NAME, prependedPlaidMetadata);
    } else if (eventStream && typeof eventStream.get('track') === 'function') {
      eventStream.track(Object.assign({
        catalog_name: CATALOG_NAME
      }, prependedPlaidMetadata));
    }
  }
});